import { Role, useGetAccessTokenPayload, useGetUserRoles } from 'auth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewHolderOnboardedModal } from '@/modules/auth/components/NewHolderOnboardedModal.tsx';
import { NEW_HOLDER_ONBOARDED_ATTRIBUTE_KEY } from '@/modules/auth/constants/onboardingNotifications.ts';
import { useGetRegisterIdParam } from '@/modules/common/hooks/useGetRegisterIdParam.ts';
import { useClearNewHolderOnboardedNotifications } from '@/modules/common/queries/organizations.ts';
import { queryClient } from '@/modules/common/query.ts';
import {
  HOLDERS_TAB_NAME,
  HoldersTabNames,
} from '@/modules/issuer-dashboard/pages/IssuerHolders.tsx';
import { ISSUER_ENTITIES_QUERY_KEY } from '@/modules/issuer-dashboard/queries/entities.ts';
import { ISSUER_HOLDERS_SUMMARY_QUERY_KEY } from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';

interface OrganizationRoleEntry {
  roles?: string[];
}

const isTruthyAttribute = (value?: string) =>
  !!value && !['false', '0', ''].includes(value.toLowerCase());

export function NewHolderOnboardedPopupController() {
  const tokenPayload = useGetAccessTokenPayload();
  const userRoles = useGetUserRoles('sr');
  const navigate = useNavigate();
  const registerId = useGetRegisterIdParam();
  const [dismissedKey, setDismissedKey] = useState('');
  const clearNotifications = useClearNewHolderOnboardedNotifications();

  const pendingRegisterIds = useMemo(() => {
    if (!userRoles.includes(Role.SR_ISSUER_ADMIN)) {
      return [];
    }

    const organizationRoles = tokenPayload?.organization_roles || {};
    const organizationAttributes = tokenPayload?.organization_attribute || {};

    return Object.entries(organizationRoles).reduce<string[]>((acc, [organizationId, data]) => {
      const roles = Array.isArray((data as OrganizationRoleEntry)?.roles)
        ? (data as OrganizationRoleEntry).roles ?? []
        : [];

      if (!roles.includes(Role.SR_ISSUER_ADMIN)) {
        return acc;
      }

      const attributeValue =
        organizationAttributes?.[organizationId]?.attributes?.[
          NEW_HOLDER_ONBOARDED_ATTRIBUTE_KEY
        ]?.[0];
      const scopedRegisterId =
        organizationAttributes?.[organizationId]?.attributes?.sr_backend_register_id?.[0];

      return isTruthyAttribute(attributeValue) && scopedRegisterId
        ? [...acc, scopedRegisterId]
        : acc;
    }, []);
  }, [tokenPayload?.organization_attribute, tokenPayload?.organization_roles, userRoles]);

  const pendingRegisterKey = useMemo(
    () => (registerId && pendingRegisterIds.includes(registerId) ? registerId : ''),
    [pendingRegisterIds, registerId],
  );

  const open = !!pendingRegisterKey && pendingRegisterKey !== dismissedKey;

  useEffect(() => {
    if (!pendingRegisterKey) {
      setDismissedKey('');
    }
  }, [pendingRegisterKey]);

  useEffect(() => {
    if (!open) {
      return;
    }

    void queryClient.invalidateQueries({ queryKey: [ISSUER_ENTITIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [ISSUER_HOLDERS_SUMMARY_QUERY_KEY] });
  }, [open]);

  const handleClose = () => {
    setDismissedKey(pendingRegisterKey);

    if (!pendingRegisterKey) {
      return;
    }

    clearNotifications.mutate();
  };

  const handleViewHolders = () => {
    handleClose();
    void queryClient.invalidateQueries({ queryKey: [ISSUER_ENTITIES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [ISSUER_HOLDERS_SUMMARY_QUERY_KEY] });
    const nextParams = new URLSearchParams(location.search);
    nextParams.set(HOLDERS_TAB_NAME, HoldersTabNames.PendingHolders);

    navigate({
      pathname: issuerDashboardPaths.static.holders,
      search: nextParams.toString(),
    });
  };

  return (
    <NewHolderOnboardedModal
      open={open}
      onViewHolders={handleViewHolders}
      isLoading={clearNotifications.isPending}
    />
  );
}
